import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@eisox/design-system";

import { m } from "~/paraglide/messages";

import type { PeriodType, PeriodsType } from "../helpers/schemas.helper";
import { AddPeriod } from "./add-period";
import { UpdatePeriod } from "./update-period";

export interface PeriodsProps extends ComponentPropsWithoutRef<"div"> {
  periods: (PeriodsType[number] & { error?: string })[];
  onAddPeriod?: (period: PeriodsType[number]) => void;
  onRemovePeriod?: (index: number) => void;
  onUpdatePeriod?: (index: number, period: PeriodsType[number]) => void;
}

export const Periods: React.FC<PeriodsProps> = ({
  periods,
  onAddPeriod,
  onRemovePeriod,
  onUpdatePeriod,
  className,
  ...props
}) => {
  const handleAddPeriod = (period: PeriodType) => onAddPeriod?.({ ...period, removed: false, added: true });
  const handleUpdatePeriod = (index: number, period: PeriodType) =>
    onUpdatePeriod?.(index, { ...period, removed: false, added: false });
  const handleRemovePeriod = (index: number, removed: boolean, added: boolean) => {
    const period = periods[index];
    if (!period) return;
    if (added) {
      onRemovePeriod?.(index);
    } else {
      onUpdatePeriod?.(index, { ...period, removed });
    }
  };

  return (
    <div
      {...props}
      className={cn("flex flex-col gap-[10px] [--header-Width:calc((100%-19px-10px-30px)/2)]", className)}
      data-testid="periods"
    >
      <div className="font-medium grid grid-cols-[var(--header-Width)_var(--header-Width)] gap-[30px] text-base text-(--color-text-secondary)">
        <span>{m.begin()}</span>
        <span>{`${m.end()} (${m.inclusive()})`}</span>
      </div>
      {periods.map((period, index) => (
        <UpdatePeriod
          key={index}
          period={period}
          onPeriodChange={updated => handleUpdatePeriod(index, updated)}
          removed={period.removed}
          onRemovedChange={removed => handleRemovePeriod(index, removed, period.added)}
          error={period.error}
        />
      ))}
      {periods.length <= 10 && <AddPeriod onAdd={handleAddPeriod} />}
    </div>
  );
};

Periods.displayName = "Periods";
